/**
 * XState state machine for avatar behavior.
 *
 * States: idle, alert, talking, thinking, happy, sad, sleeping
 *
 * Transitions:
 *   idle -> alert       (trigger: NEW_MESSAGE or NOTIFICATION)
 *   alert -> talking     (condition: isSpeaking)
 *   talking -> idle     (condition: !isSpeaking)
 *   idle -> thinking     (condition: isProcessing)
 *   thinking -> happy    (trigger: SUCCESS)
 *   thinking -> sad      (trigger: ERROR)
 *   happy -> idle        (auto after 3s)
 *   sad -> idle          (auto after 3s)
 *   idle -> sleeping     (timer: 60s no interaction)
 *   sleeping -> alert    (trigger: NEW_MESSAGE)
 *
 * The machine's current state value drives which sprite sheet the
 * AvatarAnimator plays.
 */

import { setup, type ActorRefFrom } from 'xstate';
import type { AvatarState } from '../types/avatarAnimation';

// ── Events ─────────────────────────────────────────────────────────────

export type AvatarMachineEvent =
  | { type: 'NEW_MESSAGE' }
  | { type: 'NOTIFICATION' }
  | { type: 'SPEAKING_START' }
  | { type: 'SPEAKING_STOP' }
  | { type: 'PROCESSING_START' }
  | { type: 'SUCCESS' }
  | { type: 'ERROR' }
  | { type: 'USER_ACTIVITY' };

// ── Machine definition ─────────────────────────────────────────────────

/** 60-second inactivity timer before sleeping (ms). */
const SLEEP_DELAY_MS = 60_000;

/** Auto-return delay for happy/sad states (ms). */
const MOOD_RETURN_MS = 3_000;

export const avatarMachine = setup({
  types: {
    events: {} as AvatarMachineEvent,
  },
  delays: {
    sleep_delay: SLEEP_DELAY_MS,
    mood_return: MOOD_RETURN_MS,
  },
}).createMachine({
  id: 'avatar',
  initial: 'idle',
  states: {
    idle: {
      on: {
        NEW_MESSAGE: { target: 'alert' },
        NOTIFICATION: { target: 'alert' },
        PROCESSING_START: { target: 'thinking' },
        SPEAKING_START: { target: 'talking' },
        // Self-transition with reenter to reset the sleep_delay timer.
        USER_ACTIVITY: { target: 'idle', reenter: true },
      },
      after: {
        sleep_delay: { target: 'sleeping' },
      },
    },

    alert: {
      on: {
        SPEAKING_START: { target: 'talking' },
        NOTIFICATION: { target: 'alert' }, // re-enter to re-trigger animation
        NEW_MESSAGE: { target: 'alert' },
        PROCESSING_START: { target: 'thinking' },
      },
    },

    talking: {
      on: {
        SPEAKING_STOP: { target: 'idle' },
        NEW_MESSAGE: { target: 'alert' },
        NOTIFICATION: { target: 'alert' },
      },
    },

    thinking: {
      on: {
        SUCCESS: { target: 'happy' },
        ERROR: { target: 'sad' },
      },
    },

    happy: {
      after: {
        mood_return: { target: 'idle' },
      },
      on: {
        NEW_MESSAGE: { target: 'alert' },
        NOTIFICATION: { target: 'alert' },
      },
    },

    sad: {
      after: {
        mood_return: { target: 'idle' },
      },
      on: {
        NEW_MESSAGE: { target: 'alert' },
        NOTIFICATION: { target: 'alert' },
      },
    },

    sleeping: {
      on: {
        NEW_MESSAGE: { target: 'alert' },
        NOTIFICATION: { target: 'alert' },
        USER_ACTIVITY: { target: 'idle' },
      },
    },
  },
});

// ── Helpers ────────────────────────────────────────────────────────────

export type AvatarMachineActor = ActorRefFrom<typeof avatarMachine>;

/** Map the XState snapshot value to an AvatarState for the animator. */
export function stateValueToAvatarState(value: string): AvatarState {
  const validStates: AvatarState[] = [
    'idle', 'alert', 'talking', 'thinking', 'happy', 'sad', 'sleeping',
  ];
  return validStates.includes(value as AvatarState)
    ? (value as AvatarState)
    : 'idle';
}